import React, { useEffect } from 'react';
import { TodoItemComponent } from './TodoItem';
import { SectionTodoEntry } from './SectionTodoEntry';
import { motion } from './framerMotion';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { SortType } from './SortDropdown';
import { TodoItem } from '../types/todo';

interface TodoSectionProps {
  title: string;
  Icon?: React.ElementType | null;
  todos: TodoItem[];
  sectionClass: string;
  droppableId?: string;
  showListFilter?: boolean;
  filtersList?: string | number;
  setEditingTodo: (todo: TodoItem) => void;
  highlightedTaskId?: number | null;
  onTaskInteraction?: () => void;
  highlightMessage?: string;
  onTodoUpdate?: (taskId: number, message?: string) => void;
  sortType?: SortType;
  lastDroppedId?: number | null;
  currentFilters?: { assignee: string; list: string | number; completed: 'today' | 'all' | 'hidden'; tags: string[]; search: string };
  onNewTodoCreated?: (taskId: number, title: string) => void;
  onFiltersChange?: (newFilters: { assignee: string; list: string | number; completed: 'today' | 'all' | 'hidden'; tags: string[]; search: string }) => void;
  activeDragId?: string | null;
  dragOverInfo?: { sectionId: string; index: number } | null;
  enableDnd?: boolean;
}

interface SortableTodoItemProps {
  todo: TodoItem;
  setEditingTodo: (todo: TodoItem) => void;
  highlightedTaskId?: number | null;
  onTaskInteraction?: () => void;
  highlightMessage?: string;
  onTodoUpdate?: (taskId: number, message?: string) => void;
  showListFilter?: boolean;
  filtersList?: string | number;
  sortType?: SortType;
  lastDroppedId?: number | null;
  activeDragId?: string | null;
  currentFilters?: { assignee: string; list: string | number; completed: 'today' | 'all' | 'hidden'; tags: string[]; search: string };
}

const SortableTodoItem: React.FC<SortableTodoItemProps> = ({
  todo,
  setEditingTodo,
  highlightedTaskId,
  onTaskInteraction,
  highlightMessage,
  onTodoUpdate,
  showListFilter,
  filtersList,
  sortType,
  lastDroppedId,
  activeDragId,
  currentFilters,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: todo.id,
    disabled: todo.isCompleted || todo.id < 0,
    data: {
      type: 'todo',
      todo,
    },
  });

  // Prevent editing of todos with negative (temporary) IDs
  const handleEdit = (todoToEdit: TodoItem) => {
    if (todoToEdit.id > 0) {
      setEditingTodo(todoToEdit);
    } else {
      // Could optionally show a toast notification here
      console.info('Please wait for the task to be created before editing');
    }
  };

  const isBeingDragged = isDragging || activeDragId === String(todo.id);
  const isRecentlyDropped = lastDroppedId === todo.id;

  // Key insight: We want the sortable transform for OTHER items (to create gaps)
  // But we want the dragged item itself to be invisible (since we have the drag overlay)
  const style: React.CSSProperties = {
    // Apply transform to create gaps for other items, but not for the dragged item
    transform: isBeingDragged ? undefined : CSS.Transform.toString(transform),
    transition: isBeingDragged ? undefined : transition,
    // Make dragged item completely invisible - the drag overlay shows the visual representation
    // Also keep recently dropped items invisible to prevent snap-back
    opacity: (isBeingDragged || isRecentlyDropped) ? 0 : 1,
    // Keep the space occupied so layout doesn't collapse
    visibility: (isBeingDragged || isRecentlyDropped) ? 'hidden' : 'visible',
  };

  return (
    <div ref={setNodeRef} style={style}>
      {lastDroppedId === todo.id ? (
        <TodoItemComponent
          todo={todo}
          onEdit={handleEdit}
          isHighlighted={highlightedTaskId === todo.id}
          onInteraction={onTaskInteraction}
          highlightMessage={highlightedTaskId === todo.id ? highlightMessage : undefined}
          onUpdated={onTodoUpdate}
          listFilter={showListFilter ? filtersList : undefined}
          dragHandleProps={null}
          dragAttributes={sortType === 'rank' && todo.id > 0 ? attributes : null}
          dragListeners={sortType === 'rank' && todo.id > 0 ? listeners : null}
          sortType={sortType}
          searchTerm={currentFilters?.search}
        />
      ) : (
        <motion.div layout={false}>
          <TodoItemComponent
            todo={todo}
            onEdit={handleEdit}
            isHighlighted={highlightedTaskId === todo.id}
            onInteraction={onTaskInteraction}
            highlightMessage={highlightedTaskId === todo.id ? highlightMessage : undefined}
            onUpdated={onTodoUpdate}
            listFilter={showListFilter ? filtersList : undefined}
            dragHandleProps={null}
            dragAttributes={sortType === 'rank' && todo.id > 0 ? attributes : null}
            dragListeners={sortType === 'rank' && todo.id > 0 ? listeners : null}
            sortType={sortType}
            searchTerm={currentFilters?.search}
          />
        </motion.div>
      )}
    </div>
  );
};

export const TodoSection: React.FC<TodoSectionProps> = ({
  title,
  // Icon,
  todos,
  sectionClass,
  droppableId,
  showListFilter = true,
  filtersList,
  setEditingTodo,
  highlightedTaskId,
  onTaskInteraction,
  highlightMessage,
  onTodoUpdate,
  sortType = 'rank',
  lastDroppedId,
  currentFilters,
  onNewTodoCreated,
  onFiltersChange,
  activeDragId,
  // dragOverInfo - not used in this simplified approach
  enableDnd = true,
}) => {
  // ALL HOOKS MUST BE CALLED AT THE TOP LEVEL - NO EARLY RETURNS BEFORE HOOKS
  // Store refs for each todo by id
  const itemRefs = React.useRef<{ [id: string]: HTMLDivElement | null }>({});

  // Scroll and highlight when the todo is highlighted after render
  useEffect(() => {

    
    if (highlightedTaskId && itemRefs.current[highlightedTaskId]) {
      // Always scroll to show the highlighted task, even when dialog is open
      // The key is to scroll the content area, not the whole page
      if (!highlightedTaskId) return;
      const targetElement = itemRefs.current[highlightedTaskId];
      if (!targetElement) return;

      // Try multiple approaches to find the correct scrollable container
      let scrollableContainer = document.querySelector('.mantine-AppShell-main');
      
      // Option 2: Try ScrollArea if it exists
      if (!scrollableContainer || scrollableContainer.scrollHeight <= scrollableContainer.clientHeight) {
        scrollableContainer = document.querySelector('.mantine-ScrollArea-viewport');
      }
      
      // Option 3: Look for any parent with overflow scroll
      if (!scrollableContainer || scrollableContainer.scrollHeight <= scrollableContainer.clientHeight) {
        let parent = targetElement.parentElement;
        while (parent && parent !== document.body) {
          const style = window.getComputedStyle(parent);
          if (style.overflow === 'auto' || style.overflow === 'scroll' || 
              style.overflowY === 'auto' || style.overflowY === 'scroll') {
            if (parent.scrollHeight > parent.clientHeight) {
              scrollableContainer = parent;
              break;
            }
          }
          parent = parent.parentElement;
        }
      }


      
      if (scrollableContainer && scrollableContainer.scrollHeight > scrollableContainer.clientHeight) {
        // Calculate position relative to the scrollable container
        const containerRect = scrollableContainer.getBoundingClientRect();
        const targetRect = targetElement.getBoundingClientRect();
        
        // Calculate how much to scroll to center the element
        const scrollTop = scrollableContainer.scrollTop;
        const targetTop = targetRect.top - containerRect.top + scrollTop;
        const containerHeight = containerRect.height;
        const targetHeight = targetRect.height;
        
        // Center the target in the scrollable container
        const desiredScrollTop = targetTop - (containerHeight / 2) + (targetHeight / 2);
        

        
        // Smooth scroll to the calculated position
        scrollableContainer.scrollTo({
          top: Math.max(0, desiredScrollTop),
          behavior: 'smooth'
        });
      } else {
        // Fallback to default behavior if we can't find a scrollable container
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [highlightedTaskId]);

  // Defensive: ensure todos is always an array
  const safeTodos = Array.isArray(todos) ? todos : [];

  // Map section title to section type for SectionTodoEntry
  const getSectionType = (): 'today' | 'tomorrow' | 'thisWeekend' | 'later' | 'overdue' | 'noDueDate' | null => {
    switch (title) {
      case 'TODAY':
        return 'today';
      case 'TOMORROW':
        return 'tomorrow';
      case 'THIS WEEKEND':
        return 'thisWeekend';
      case 'LATER':
        return 'later';
      case 'OVERDUE':
        return 'overdue';
      case 'NO DUE DATE':
        return 'noDueDate';
      case 'COMPLETED':
        return null; // Don't show entry for completed section
      default:
        return null;
    }
  };

  const sectionType = getSectionType();

  // Don't render section if no todos and no section type for adding
  if (safeTodos.length === 0 && !sectionType) return null;

  if (!enableDnd) {
    return (
      <div 
        className={`todo-section ${sectionClass}`} 
        style={{ 
          position: 'relative', 
          minHeight: '60px',
          padding: '10px 0'
        }}
      >
        {safeTodos.length > 0 ? (
          <div className="todo-items">
            {safeTodos.map((todo: TodoItem) => {
              const setItemRef = (el: HTMLDivElement | null) => {
                itemRefs.current[todo.id] = el;
              };

              return (
                <div key={todo.id} ref={setItemRef}>
                  <motion.div layout={false}>
                    <TodoItemComponent
                      todo={todo}
                      onEdit={(todoToEdit) => {
                        if (todoToEdit.id > 0) {
                          setEditingTodo(todoToEdit);
                        }
                      }}
                      isHighlighted={highlightedTaskId === todo.id}
                      onInteraction={onTaskInteraction}
                      highlightMessage={highlightedTaskId === todo.id ? highlightMessage : undefined}
                      onUpdated={onTodoUpdate}
                      listFilter={showListFilter ? filtersList : undefined}
                      dragHandleProps={null}
                      dragAttributes={null}
                      dragListeners={null}
                      sortType={sortType}
                      searchTerm={currentFilters?.search}
                    />
                  </motion.div>
                </div>
              );
            })}
          </div>
        ) : (
          <div 
            style={{ 
              minHeight: '60px',
              padding: '20px',
              border: '2px dashed var(--app-border)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--app-text-muted)',
              fontSize: '14px',
              backgroundColor: 'transparent',
            }}
          >
            <span>Add tasks above</span>
          </div>
        )}

        {sectionType && (
          <SectionTodoEntry
            sectionType={sectionType}
            currentFilters={currentFilters}
            onNewTodoCreated={onNewTodoCreated}
            onFiltersChange={onFiltersChange}
          />
        )}
      </div>
    );
  }

  const { setNodeRef } = useDroppable({
    id: droppableId || 'droppable-section',
  });

  return (
    <div 
      className={`todo-section ${sectionClass}`} 
      ref={setNodeRef}
      style={{ 
        position: 'relative', 
        minHeight: '60px',
        padding: '10px 0'
      }}
    >
      {safeTodos.length > 0 ? (
        <SortableContext
          items={safeTodos.map(todo => todo.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="todo-items">
            {safeTodos.map((todo: TodoItem) => {
              // Set ref for this todo for scrolling
              const setItemRef = (el: HTMLDivElement | null) => {
                itemRefs.current[todo.id] = el;
              };

              return (
                <div key={todo.id} ref={setItemRef}>
                  <SortableTodoItem
                    todo={todo}
                    setEditingTodo={setEditingTodo}
                    highlightedTaskId={highlightedTaskId}
                    onTaskInteraction={onTaskInteraction}
                    highlightMessage={highlightMessage}
                    onTodoUpdate={onTodoUpdate}
                    showListFilter={showListFilter}
                    filtersList={filtersList}
                    sortType={sortType}
                    lastDroppedId={lastDroppedId}
                    activeDragId={activeDragId}
                    currentFilters={currentFilters}
                  />
                </div>
              );
            })}
          </div>
        </SortableContext>
      ) : (
        // Empty section drop zone
        <div 
          style={{ 
            minHeight: '60px',
            padding: '20px',
            border: '2px dashed var(--app-border)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--app-text-muted)',
            fontSize: '14px',
            transition: 'all 0.2s ease',
            backgroundColor: 'transparent',
          }}
        >
          <span>Drop items here</span>
        </div>
      )}
      
      {/* Add section-specific todo entry */}
      {sectionType && (
        <SectionTodoEntry
          sectionType={sectionType}
          currentFilters={currentFilters}
          onNewTodoCreated={onNewTodoCreated}
          onFiltersChange={onFiltersChange}
        />
      )}
    </div>
  );
};
